import { useEffect } from 'react'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Avatar,
  Banner,
  ButtonLink,
  EmptyState,
  Eyebrow,
  Icon,
  PlateSkeleton,
  StatStrip,
  useGround,
} from '@/design-system'
import { getHomeSummary, getNearbyKnownCats } from '@/services/api/sightings'
import { getCollection } from '@/services/api/cats'
import { useSession } from '@/stores/session'
import { useLocation } from '@/hooks/useLocation'
import { useQueue } from '@/features/offline/queue'
import { flags } from '@/app/config'
import { formatCount, formatDateShort, initials } from '@/utils/format'
import { avatarSrc } from '@/features/social/avatars'
import { CatPlate } from './CatPlate'
import { CatOfTheDayCard } from './CatOfTheDayCard'
import { FirstMissionCard } from './FirstMissionCard'
import { StreakChip } from './StreakChip'
import { NearbyList } from './NearbyList'
import './home.css'

const TODAY = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

function today() {
  const text = TODAY.format(new Date()).replace(',', '')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function HomeScreen() {
  useGround('candy')
  const profile = useSession((s) => s.profile)
  const user = useSession((s) => s.user)
  const location = useLocation(true)
  const pendingDrafts = useQueue((s) => s.pending)

  const summary = useQuery({
    queryKey: ['home-summary', user?.id],
    queryFn: getHomeSummary,
    enabled: Boolean(user),
  })

  const collection = useQuery({
    queryKey: ['collection', user?.id, 'peek'],
    queryFn: () => getCollection(undefined, 12),
    enabled: Boolean(user),
  })

  const fix = location.status === 'granted' ? location.fix : null
  const nearby = useQuery({
    // Rounded so a metre of drift does not refetch the list constantly.
    queryKey: ['nearby', fix ? Math.round(fix.lat * 2000) : null, fix ? Math.round(fix.lng * 2000) : null],
    queryFn: () => getNearbyKnownCats(fix!.lat, fix!.lng, 800, 6),
    enabled: Boolean(fix),
    staleTime: 60_000,
  })

  const stats = summary.data
  const isEmpty = stats != null && stats.cats_count === 0

  // La bienvenida existía y no la veía nadie (§auditoría H94): el registro solo
  // llevaba a /bienvenida cuando NO hacía falta confirmar el correo, y hace
  // falta siempre. Se la enseñamos una vez a quien llega con la casa vacía, que
  // es justo cuando sirve: explica de qué va esto y pide el permiso de
  // ubicación antes de la primera captura.
  const navigate = useNavigate()
  useEffect(() => {
    if (!stats || stats.cats_count > 0 || stats.sightings_count > 0) return
    try {
      if (localStorage.getItem('catgo.onboarded')) return
      localStorage.setItem('catgo.onboarded', new Date().toISOString())
    } catch {
      return // sin memoria, mejor no insistir en cada arranque
    }
    navigate('/bienvenida', { replace: true })
  }, [stats, navigate])
  const firstName = profile?.display_name.split(' ')[0]

  return (
    <main className="page">
      <header className="home__head">
        <div>
          <p className="home__date">{today()}</p>
          <h1 className="home__title">{firstName ? 'Hola, ' + firstName : 'Hola'}</h1>
          <div style={{ marginBlockStart: 'var(--s2)' }}>
            <StreakChip />
          </div>
        </div>
        {profile && (
          <div className="home__acciones">
            <NotificationBell />
            <Link to="/perfil" className="home__avatar" aria-label="Tu perfil">
              <Avatar
                fallback={initials(profile.display_name)}
                src={avatarSrc(profile.avatar_key)}
                size="md"
              />
            </Link>
          </div>
        )}
      </header>

      {/* Quien aún no ha conocido a ningún gato recibe UNA instrucción, no dos:
          la misión sustituye al gato del día, que todavía no tiene a quién
          enseñar. (§auditoría H202) */}
      {stats && stats.sightings_count === 0 ? (
        <FirstMissionCard tieneGatos={stats.cats_count > 0} />
      ) : (
        <div style={{ marginBlock: 'var(--s4) var(--s5)' }}>
          <CatOfTheDayCard />
        </div>
      )}

      {stats && !isEmpty && (
        <StatStrip
          stats={[
            { value: formatCount(stats.cats_count), label: stats.cats_count === 1 ? 'gato' : 'gatos' },
            { value: formatCount(stats.sightings_count), label: stats.sightings_count === 1 ? 'encuentro' : 'encuentros' },
            { value: formatCount(stats.places_count), label: stats.places_count === 1 ? 'lugar' : 'lugares' },
          ]}
        />
      )}

      <div className="stack stack--3" style={{ marginBlockStart: 'var(--s4)' }}>
        {pendingDrafts > 0 && (
          <Banner
            icon="cloud"
            title="Pendiente de sincronizar"
            action={
              <ButtonLink to="/pendientes" variant="secondary" size="sm">
                Ver pendientes
              </ButtonLink>
            }
          >
            {pendingDrafts === 1
              ? 'Hay 1 encuentro guardado en este teléfono esperando conexión.'
              : 'Hay ' + pendingDrafts + ' encuentros guardados en este teléfono esperando conexión.'}
          </Banner>
        )}

        {stats && stats.pending_requests > 0 && (
          <Banner
            icon="users"
            tone="accent"
            title="Solicitudes de seguimiento"
            action={
              <ButtonLink to="/perfil/solicitudes" variant="secondary" size="sm">
                Revisar
              </ButtonLink>
            }
          >
            {stats.pending_requests === 1
              ? 'Una persona quiere entrar en tu colección.'
              : stats.pending_requests + ' personas quieren entrar en tu colección.'}
          </Banner>
        )}

        {stats && stats.possible_duplicates > 0 && (
          <Banner
            icon="link"
            title="¿El mismo gato?"
            action={
              <ButtonLink to="/duplicados" variant="secondary" size="sm">
                Comparar
              </ButtonLink>
            }
          >
            {stats.possible_duplicates === 1
              ? 'Hay dos fichas que podrían ser el mismo animal.'
              : 'Hay ' + stats.possible_duplicates + ' pares de fichas que podrían ser el mismo animal.'}
          </Banner>
        )}
      </div>

      {/* Con la misión arriba sobra el segundo mensaje pidiendo lo mismo, pero
          solo cuando no hay NADA que enseñar. Quien tiene gatos de casa y aún
          no ha salido a la calle sigue viendo los suyos: son suyos. */}
      {stats && stats.sightings_count === 0 && isEmpty ? null : isEmpty ? (
        <div className="glass" style={{ marginBlockStart: 'var(--s5)' }}>
          <EmptyState
            title="Tu colección está esperando"
            body="La próxima vez que te cruces con un gato, hazle una foto. Quedará guardado con el lugar y el día en que lo conociste."
            action={
              <div className="stack stack--2" style={{ alignItems: 'center' }}>
                <ButtonLink to="/capturar" size="lg" icon="cat-camera">
                  Encontré un gato
                </ButtonLink>
                <ButtonLink to="/capturar?casa=1" variant="ghost" size="sm" icon="home">
                  Añadir un gato de casa
                </ButtonLink>
                {flags.discover && (
                  <ButtonLink to="/descubrir" variant="ghost" size="sm" icon="compass">
                    Ver quién más registra gatos
                  </ButtonLink>
                )}
              </div>
            }
          />
        </div>
      ) : (
        <>
          <Eyebrow action="Ver todos" to="/coleccion">
            Tus últimos gatos
          </Eyebrow>

          {collection.isLoading ? (
            <div className="rail">
              <PlateSkeleton width="9.25rem" />
              <PlateSkeleton width="9.25rem" />
              <PlateSkeleton width="9.25rem" />
            </div>
          ) : (
            <div className="rail">
              {collection.data?.map((cat) => (
                <CatPlate
                  key={cat.cat_id}
                  catId={cat.cat_id}
                  catalogNo={cat.catalog_no}
                  name={cat.name}
                  date={cat.last_seen_at ?? cat.first_met_at}
                  coverMediaId={cat.cover_media_id}
                  sightingCount={cat.sighting_count}
                  place={cat.last_place}
                  household={cat.household}
                  width="rail"
                />
              ))}
            </div>
          )}
        </>
      )}

      {fix && nearby.data && nearby.data.length > 0 && (
        <div className="glass nearby" style={{ marginBlockStart: 'var(--s5)' }}>
          <h2 className="nearby__title">
            <Icon name="pin" size={20} />
            Gatos cerca de ti
          </h2>
          <NearbyList cats={nearby.data} />
        </div>
      )}

      {location.status === 'denied' && !isEmpty && (
        <div style={{ marginBlockStart: 'var(--s5)' }}>
          <Banner icon="pin" title="Ubicación desactivada">
            Sin ubicación no podemos recordar dónde conociste a cada gato, ni avisarte
            cuando estés cerca de uno que ya conoces.
          </Banner>
        </div>
      )}

      {stats && stats.recent.length > 0 && (
        <>
          <Eyebrow>Actividad reciente</Eyebrow>
          <div className="glass activity">
            {stats.recent.slice(0, 5).map((entry) => (
              <Link key={entry.sighting_id} to={'/gato/' + entry.cat_id} className="activity__row">
                <span className="activity__date">{formatDateShort(entry.observed_at)}</span>
                <span className="activity__text">
                  {entry.is_first ? 'Conociste a ' : 'Volviste a ver a '}
                  <strong>{entry.name ?? 'un gato sin nombre'}</strong>
                  {entry.city ? ' · ' + entry.city : ''}
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
    </main>
  )
}
